import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Button, Drawer, Link, Spinner, Table, Tag, TagGroup } from '@heroui/react'
import { api } from '../lib/api'
import { latestRead } from '../lib/latestRead'
import { explain } from '../lib/staffErrors'
import type { Branch, ReportView, TicketReportRow, TicketReportSummary } from '../lib/types'
import { Icon } from '../ui/icons'
import { NoteHistory } from '../ui/NoteHistory'
import { useStaffText } from '../ui/StaffText'
import { chipsOf, periodFor, withoutChip } from './drill'
import { duration, group, isoToday, periodRange, type PeriodId } from './insightCalc'
import { ExportMenu, PeriodMenu } from './ReportBar'
import { Find, Notice, Pick, PickMany, type PickOption } from './kit'
import { SavedViews, type ToastFn } from './SavedViews'
import { branchScope, COLUMNS, RESULTS, RESULT_KEY, pagingFor, redirectedText, ticketQuery, timeOf, type Paging, type TicketFilters } from './ticketReport'
import { fromSpec, sameSpec, ticketsSpec } from './views'

/**
 * The per-ticket report: every piece of work on every ticket, one row each, in the
 * columns VTB's tender lists. The rows are the server's, and so are the two files:
 * a year of a busy network is more than a browser should build, so Excel and CSV
 * are links to what the server streams.
 */

export type ReportTab = 'figures' | 'tickets' | 'bookings'

const PAGE = 100

export function Tickets({
  branches,
  branchId,
  lead,
  onMail,
  initial,
  names,
  onFigures,
  backLabel,
  figure,
  shared,
  views,
  viewsReady,
  viewsProblem,
  onViews,
  onReloadViews,
  organisation,
  toast,
}: {
  branches: Branch[]
  branchId: string
  /** First in the bar: the Questions button where the rail is folded away. */
  lead: ReactNode
  /** Opens the reports by email. */
  onMail: () => void
  /** The saved Tickets views this person sees, and the list after a change. */
  views: ReportView[]
  viewsReady: boolean
  /** Shown instead of the views when they could not be read. */
  viewsProblem?: ReactNode
  onViews: (next: ReportView[]) => void
  onReloadViews: () => void
  /** The organisation a view is saved in, for the installation's owner. */
  organisation?: string
  toast: ToastFn
  /** A clicked figure's filters: the tab opens on exactly its tickets. */
  initial?: TicketFilters
  /** Words the click knew for filters that hold an id (a desk's name). */
  names?: Partial<Record<keyof TicketFilters, string>>
  /** Back to the figures, as they were left; only when a figure opened this. */
  onFigures?: () => void
  /** The tab the figure was clicked on, when not Figures. */
  backLabel?: string
  /** Which count the clicked figure was, drawn first-hand on the count line. */
  figure?: 'tickets' | 'rows'
  /** The period in the bar, shared with Figures and Bookings, when no figure opened this. */
  shared?: { period: PeriodId; custom: [string, string]; onPeriod: (p: PeriodId) => void; onCustom: (c: [string, string]) => void }
}) {
  const { lang, say } = useStaffText()
  const today = isoToday()
  const [start] = useState(() => (initial ? periodFor(initial.from, initial.to, today) : null))
  const [notesOf, setNotesOf] = useState<TicketReportRow | null>(null)
  const [chosen, setChosen] = useState<string[]>(() => (initial?.branches.length ? initial.branches : branchId ? [branchId] : []))
  const [ownPeriod, setOwnPeriod] = useState<PeriodId>(start?.period ?? 'today')
  const [ownCustom, setOwnCustom] = useState<[string, string]>(start?.custom ?? [today, today])
  // Opened from the rail, the period is the bar's; a figure's tickets keep the figure's own.
  const period = shared?.period ?? ownPeriod
  const custom = shared?.custom ?? ownCustom
  const setPeriod = shared?.onPeriod ?? setOwnPeriod
  const setCustom = shared?.onCustom ?? setOwnCustom
  const [service, setService] = useState(initial?.service ?? '')
  const [staff, setStaff] = useState(initial?.staff ?? '')
  const [result, setResult] = useState(initial?.result ?? '')
  const [channel, setChannel] = useState(initial?.channel ?? '')
  // The drill's filters that have no slicer here: each is a chip until taken off.
  const [extra, setExtra] = useState<Partial<TicketFilters>>(() => initial ?? {})
  // What the chips call filters that hold an id: the click's words, or a saved view's.
  const [named, setNamed] = useState<Partial<Record<keyof TicketFilters, string>>>(() => names ?? {})
  // The question the figure asked: its count is drawn first-hand only while it is still the one shown.
  const [asked] = useState(() => (initial ? ticketQuery(initial) : ''))
  const [paging, setPaging] = useState<Paging>({ query: '', cursors: [''] })
  const [rows, setRows] = useState<TicketReportRow[]>([])
  const [next, setNext] = useState<string | undefined>()
  const [summary, setSummary] = useState<TicketReportSummary | null>(null)
  // A figure's tickets take the focus: the main area at once, then the count line
  // once it is read, unless the person has moved on meanwhile.
  const mainRef = useRef<HTMLElement>(null)
  const countRef = useRef<HTMLParagraphElement>(null)
  const counted = useRef(false)
  useEffect(() => {
    if (initial) mainRef.current?.focus({ preventScroll: true })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initial || !summary || counted.current) return
    counted.current = true
    const at = document.activeElement
    if (!at || at === document.body || at === mainRef.current) countRef.current?.focus({ preventScroll: true })
  }, [summary]) // eslint-disable-line react-hooks/exhaustive-deps
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  // The box as typed, and the search it settles into after a pause.
  const [typed, setTyped] = useState('')
  const [search, setSearch] = useState('')
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(typed.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [typed])

  // The branch list can arrive after this tab is open.
  useEffect(() => {
    if (chosen.length === 0 && branchId) setChosen([branchId])
  }, [branchId, chosen.length])

  const [from, to] = periodRange(period, today, custom)
  const badDates = period === 'custom' && (!from || !to || from > to)
  const ready = chosen.length > 0 && !badDates
  const filters: TicketFilters = { ...extra, branches: branchScope(chosen, branches), from, to, service, staff, result, channel }
  const chips = chipsOf(filters, say, lang, named)
  const here = ticketsSpec({ filters, period, names: named })
  const currentView = views.find((v) => sameSpec(v.spec, { ...here, period: v.spec.period === '' ? '' : here.period }))
  const applyView = (v: ReportView) => {
    const t = fromSpec(v.spec)?.tickets
    if (!t) return
    // Marks and outcomes are switched off: an older view's filter on them is dropped, not hidden.
    const { service: sv = '', staff: st = '', result: rs = '', channel: ch = '', names: words = {}, outcome: _o, mark: _m, ...rest } = t
    setService(sv)
    setStaff(st)
    setResult(rs)
    setChannel(ch)
    setExtra(rest)
    setNamed(Object.fromEntries(Object.entries(words).filter(([k]) => k !== 'outcome' && k !== 'mark')))
    const p = fromSpec(v.spec)?.period
    if (p) setPeriod(p)
  }
  const query = ticketQuery({ ...filters, number: search })
  const lit = figure && query === asked ? figure : undefined
  // A different question starts again at its first page: pagingFor reads page 1
  // when the filters, period or branch no longer match the question `paging`
  // was built for.
  const paged = pagingFor(paging, query)
  const cursors = paged.cursors
  const after = cursors[cursors.length - 1] ?? ''

  // Written back as state, not just read, so a later return to this same
  // question does not find an in-between question's cursor stack still sitting
  // there — that stale-state case is what used to land a reader back on an old
  // page (e.g. Last 7 days → Yesterday → Last 7 days).
  useEffect(() => {
    if (paging.query !== query) setPaging(paged)
  }, [query]) // eslint-disable-line react-hooks/exhaustive-deps

  // A slicer change runs the cleanup, which aborts the read it supersedes.
  useEffect(() => {
    if (!ready) return
    setBusy(true)
    setError('')
    return latestRead(
      (signal) =>
        Promise.all([
          api.ticketReport(ticketQuery({ ...filters, number: search }, after, PAGE), signal),
          after === '' ? api.ticketReportSummary(query, signal) : Promise.resolve(null),
        ]),
      {
        done: ([page, counted]) => {
          setRows(page.rows)
          setNext(page.next)
          if (counted) setSummary(counted)
        },
        failed: (err) => {
          setError(explain(err, lang, 'common.unreachable'))
          setRows([])
          setNext(undefined)
        },
        settled: () => setBusy(false),
      },
    )
  }, [query, after, ready, lang]) // eslint-disable-line react-hooks/exhaustive-deps

  const branchLabel =
    chosen.length === 1 ? (branches.find((b) => b.id === chosen[0])?.name ?? '') : say('tickets.branchesN', { n: chosen.length })
  const all = { value: '', label: say('tickets.all') }
  const slicer = (name: string, value: string, onChange: (v: string) => void, options: PickOption[]) => (
    <Pick label={name} caption={name} value={value} onChange={onChange} options={[all, ...options]} />
  )

  return (
    <div className="insight">
      <div className="insight-top">
        <div className="insight-top__row">
          {onFigures && (
            <Button variant="ghost" size="sm" onPress={onFigures}>
              <Icon name="left" size={16} /> {backLabel ?? say('tickets.tabFigures')}
            </Button>
          )}
          {lead}
          <PickMany
            label={say('common.branch')}
            caption={say('common.branch')}
            summary={branchLabel}
            value={chosen}
            onChange={setChosen}
            options={branches.map((b) => ({ value: b.id, label: b.name }))}
          />
          <PeriodMenu period={period} onPeriod={setPeriod} custom={custom} onCustom={setCustom} today={today} bad={badDates} />
          <Find className="insight-ticketfind" label={say('tickets.search')} value={typed} onChange={setTyped} />
          <span className="insight-spacer" />
          <div className="insight-actions">
            {busy && (
              <span className="insight-busy">
                <Spinner size="sm" />
                {say('common.reading')}
              </span>
            )}
            {/* The server's files: a year of a busy network is more than a browser should build. */}
            <ExportMenu
              items={[
                { label: say('tickets.excel'), icon: 'table', href: ready ? api.ticketReportFile(query, 'xlsx') : undefined },
                { label: say('tickets.csv'), icon: 'table', href: ready ? api.ticketReportFile(query, 'csv') : undefined },
                'rule',
                { label: say('reportMail.title'), icon: 'mail', run: onMail },
              ]}
            />
          </div>
        </div>
        <div className="insight-top__row insight-top__row--2">
          <div className="insight-viewbar">
            {viewsProblem ?? (
              <SavedViews
                views={views}
                current={currentView}
                draft={(name, shared) => ({ name, kind: 'tickets', shared, spec: here, organisation })}
                canSave={viewsReady && !currentView}
                onApply={applyView}
                onList={onViews}
                onReload={onReloadViews}
                toast={toast}
              />
            )}
          </div>
          {slicer(say('tickets.service'), service, setService, (summary?.services ?? []).map((s) => ({ value: s.id, label: s.name })))}
          {slicer(say('tickets.employee'), staff, setStaff, (summary?.staff ?? []).map((s) => ({ value: s.id, label: s.name })))}
          {slicer(say('tickets.result'), result, setResult, RESULTS.map((r) => ({ value: r, label: say(RESULT_KEY[r]) })))}
          {slicer(say('tickets.channel'), channel, setChannel, [
            { value: 'online', label: say('tickets.online') },
            { value: 'branch', label: say('tickets.inBranch') },
          ])}
          {chips.length > 0 && (
            <TagGroup
              aria-label={say(chips.length === 1 ? 'pivot.oneFilter' : 'pivot.nFilters', { n: chips.length })}
              size="sm"
              className="rp-tags insight-drillchips"
              aria-live="polite"
              onRemove={(keys) => {
                const c = chips.find((x) => keys.has(x.id))
                if (!c) return
                if (c.id === 'staff') setStaff('')
                setExtra(withoutChip(filters, c.id))
              }}
            >
              <TagGroup.List>
                {chips.map((c) => {
                  const text = c.name ? `${c.name} ${c.text}` : c.text
                  return (
                    <Tag key={c.id} id={c.id} textValue={text}>
                      {c.name && <span className="insight-fchip__name">{c.name}</span>}
                      <span className="insight-fchip__value" title={c.text}>
                        {c.text}
                      </span>
                      <Tag.RemoveButton aria-label={say('insight.removeField', { name: text })} />
                    </Tag>
                  )
                })}
              </TagGroup.List>
            </TagGroup>
          )}
        </div>
      </div>

      <div className="insight-body">
        <main className="insight-main" data-insight-scroller ref={mainRef} tabIndex={-1}>
          {badDates ? (
            <p className="insight-problem" role="alert">
              {say('insight.badDates')}
            </p>
          ) : error ? (
            <Notice>{error}</Notice>
          ) : (
            <>
              {summary && (
                <p className="insight-count" ref={countRef} tabIndex={-1}>
                  <span data-figure={lit === 'tickets' || undefined}>{say('tickets.countTickets', { n: group(summary.tickets), count: summary.tickets })}</span> ·{' '}
                  <span data-figure={lit === 'rows' || undefined}>{say('tickets.countRows', { n: group(summary.steps), count: summary.steps })}</span>
                </p>
              )}
              {rows.length === 0 ? (
                !busy && <p className="insight-muted">{say('tickets.none')}</p>
              ) : (
                <Table className="insight-grid">
                  <Table.ScrollContainer>
                    <Table.Content aria-label={say('tickets.tabTickets')}>
                      <Table.Header>
                        {COLUMNS.map((k, i) => (
                          <Table.Column key={k} id={k} isRowHeader={i === 4} className={i === 9 || i === 10 ? 'insight-grid__num' : undefined}>
                            {say(k)}
                          </Table.Column>
                        ))}
                      </Table.Header>
                      <Table.Body>
                        {rows.map((r) => (
                          <Table.Row key={`${r.ticketId}-${r.step}`} id={`${r.ticketId}-${r.step}`}>
                            <Table.Cell>{r.branch}</Table.Cell>
                            <Table.Cell>{r.employee ?? ''}</Table.Cell>
                            <Table.Cell>{r.section}</Table.Cell>
                            <Table.Cell>{r.desk ?? ''}</Table.Cell>
                            <Table.Cell>
                              <Link className="insight-number" aria-label={`${say('floor.notes')} · ${r.number}`} onPress={() => setNotesOf(r)}>
                                {r.number}
                              </Link>
                            </Table.Cell>
                            <Table.Cell>{r.date}</Table.Cell>
                            <Table.Cell>{timeOf(r.takenAt, r.timezone)}</Table.Cell>
                            <Table.Cell>{timeOf(r.startedAt, r.timezone)}</Table.Cell>
                            <Table.Cell>{timeOf(r.endedAt, r.timezone)}</Table.Cell>
                            {/* In words with units on screen; the files keep the clock a spreadsheet sums. */}
                            <Table.Cell className="insight-grid__num">{r.serviceSec === undefined ? '' : duration(r.serviceSec, lang)}</Table.Cell>
                            <Table.Cell className="insight-grid__num">{r.waitSec === undefined ? '' : duration(r.waitSec, lang)}</Table.Cell>
                            <Table.Cell>{say(RESULT_KEY[r.result])}</Table.Cell>
                            <Table.Cell>{redirectedText(r, say)}</Table.Cell>
                            <Table.Cell>{r.redirectedBy ?? ''}</Table.Cell>
                            <Table.Cell>{r.redirectedTo ?? ''}</Table.Cell>
                            <Table.Cell>{say(r.online ? 'tickets.online' : 'tickets.inBranch')}</Table.Cell>
                            <Table.Cell>{r.viaToken ?? ''}</Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Content>
                  </Table.ScrollContainer>
                </Table>
              )}
              {(cursors.length > 1 || next) && (
                <div className="insight-actions">
                  <Button variant="secondary" size="sm" isDisabled={cursors.length < 2 || busy} onPress={() => setPaging({ query, cursors: cursors.slice(0, -1) })}>
                    {say('tickets.previous')}
                  </Button>
                  <Button variant="secondary" size="sm" isDisabled={!next || busy} onPress={() => next && setPaging({ query, cursors: [...cursors, next] })}>
                    {say('tickets.next')}
                  </Button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
      {notesOf && (
        <Drawer.Backdrop isOpen onOpenChange={(open) => !open && setNotesOf(null)}>
          <Drawer.Content placement="right" className="rp-drawer">
            <Drawer.Dialog>
              <Drawer.CloseTrigger aria-label={say('ui.close')} />
              <Drawer.Header>
                <Drawer.Heading>{`${say('floor.notes')} · ${notesOf.number}`}</Drawer.Heading>
              </Drawer.Header>
              <Drawer.Body>
                <NoteHistory ticket={{ id: notesOf.ticketId, state: 'closed' }} zone={notesOf.timezone} adds={false} edits={false} label="" />
              </Drawer.Body>
            </Drawer.Dialog>
          </Drawer.Content>
        </Drawer.Backdrop>
      )}
    </div>
  )
}
